// L'e-mail de la pré-demande de dérogation (D-083), envoyé au club adverse par push-dispatch :
// « <Club> via Arceau », réponses au bureau qui l'envoie (Reply-To), un lien « Répondre en 1 clic »
// vers la page sans compte, et le pied « Envoyé avec Arceau · Ne plus recevoir ces messages ».
// Texte brut (sendMail), vouvoiement. Logique pure : testée sans réseau.

export interface PreMail {
  id: string; token: string; to: string; reply_to: string | null; signer: string | null; reminder: boolean;
  club_name: string; timezone: string | null; team: string; opponent: string; is_home: boolean | null;
  competition: string | null; competition_name: string | null; kind: 'moved' | 'swap';
  starts_at: string; location: string | null; pair_starts_at: string | null; pair_location: string | null;
  slots: { starts_at: string; location: string | null }[];
  reason: string; reason_note: string | null; opponent_club: string;
  referral_code?: string | null;   // le lien de parrainage du club qui demande (D-087), relu par push-dispatch
  // D-137 : une demande reçue (« in ») : notre réponse ; l'inversion datée (slots[0] : ce match, pair_slot : le retour).
  direction?: 'out' | 'in'; pair_slot?: { starts_at: string; location: string | null } | null;
  our_answer?: 'accept' | 'refuse' | null; our_answer_note?: string | null;
  // D-146 : « Les prévenir » (un créneau, « nous allons saisir ce changement sur FBI ») ou « Demander leur accord ».
  mode?: 'accord' | 'prevenir';
}

const REASON: Record<string, string> = {
  salle: 'salle indisponible', effectif: 'question d’effectif', deplacement: 'contrainte de déplacement', autre: '',
};
const COMPETITION: Record<string, string> = {
  championnat: 'Championnat', coupe: 'Coupe', phase_finale: 'Phase finale', brassage: 'Brassage', amical: 'Match amical',
};

const fmt = (iso: string, tz: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', { timeZone: tz, ...o }).format(new Date(iso));
function hm(iso: string, tz: string): string {
  const [h, m] = fmt(iso, tz, { hour: '2-digit', minute: '2-digit', hour12: false }).split(':');
  return `${Number(h)}h${m === '00' ? '' : m}`;
}
/** « samedi 31 octobre, 20h30 » */
const longWhen = (iso: string, tz: string) => `${fmt(iso, tz, { weekday: 'long', day: 'numeric', month: 'long' })}, ${hm(iso, tz)}`;
/** « Gymnase pris… » → « gymnase pris… » au fil d'une phrase (« U11 », « FFBB » restent). */
const lower = (s: string) => (/^\p{Lu}\p{Ll}/u.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const ofClub = (name: string) => (/^[aeiouyhàâéèêëîïôöûü]/i.test(name.trim()) ? `de l’${name.trim()}` : `du ${name.trim()}`);

export interface BuiltMail { subject: string; text: string; fromName: string; footer: string; unsubscribe: string; link: string }

export function derogationMail(m: PreMail, appUrl: string): BuiltMail {
  if (m.direction === 'in') return answerMail(m, appUrl);
  if (m.mode === 'prevenir' && m.slots[0]) return prevMail(m, appUrl);
  const tz = m.timezone || 'Europe/Paris';
  const base = appUrl.replace(/\/+$/, '');
  const link = `${base}/d/${m.token}`;
  const unsubscribe = `${link}?stop=1`;
  const title = `${m.team} ${m.is_home === false ? 'à' : '–'} ${m.opponent}`;
  const day = fmt(m.starts_at, tz, { weekday: 'short', day: 'numeric', month: 'short' });
  const swap = m.kind === 'swap';
  const subject = `${m.reminder ? 'Rappel : ' : ''}${m.club_name} vous propose ${swap ? `d’inverser domicile et extérieur pour ${title}` : `de déplacer ${title} du ${day}`}`;

  const competition = m.competition_name || COMPETITION[m.competition ?? ''] || '';
  const reason = [REASON[m.reason] ?? '', lower(m.reason_note?.trim() ?? '')].filter(Boolean);
  const why = reason.length === 2 ? `${reason[0]} (${reason[1]})` : reason[0] ?? '';
  const dated = swap && (m.slots.length > 0 || !!m.pair_slot);
  const at = (iso: string) => longWhen(iso, tz);
  const proposals = dated
    ? [
        'Nous vous proposons d’inverser domicile et extérieur, avec de nouvelles dates :',
        `  • ${at(m.slots[0]?.starts_at ?? m.starts_at)} : chez ${m.is_home ? 'vous' : 'nous'}${m.slots[0]?.location ? ` (${m.slots[0].location})` : ''}, au lieu du ${at(m.starts_at)}`,
        ...(m.pair_starts_at ? [`  • ${at(m.pair_slot?.starts_at ?? m.pair_starts_at)} : chez ${m.is_home ? 'nous' : 'vous'}${m.pair_slot?.location ? ` (${m.pair_slot.location})` : ''}, au lieu du ${at(m.pair_starts_at)}`] : []),
      ]
    : swap
    ? [
        'Nous vous proposons d’inverser domicile et extérieur, aux mêmes dates :',
        `  • ${longWhen(m.starts_at, tz)} : chez ${m.is_home ? 'vous' : 'nous'}`,
        ...(m.pair_starts_at ? [`  • ${longWhen(m.pair_starts_at, tz)} : chez ${m.is_home ? 'nous' : 'vous'}`] : []),
      ]
    : [
        'Nous vous proposons :',
        ...m.slots.map(s => `  • ${longWhen(s.starts_at, tz)}${s.location ? ` · ${s.location}` : ''}`),
      ];
  const signer = m.signer && m.signer !== 'Compte' ? `${m.signer}, pour le bureau ${ofClub(m.club_name)}` : `Le bureau ${ofClub(m.club_name)}`;
  const text = [
    'Bonjour,',
    '',
    ...(m.reminder ? ['Petit rappel : nous attendons votre réponse à cette demande.', ''] : []),
    `Nous souhaitons ${swap ? 'changer' : 'déplacer'} cette rencontre :`,
    '',
    `    ${title}`,
    `    ${[competition, longWhen(m.starts_at, tz)].filter(Boolean).join(' · ')}`,
    ...(m.location ? [`    ${m.location}`] : []),
    '',
    ...(why ? [`Pourquoi : ${why}.`, ''] : []),
    ...proposals,
    '',
    'Répondre en 1 clic :',
    link,
    '',
    `Sans compte, en 30 secondes. Nous saisissons ensuite la demande de dérogation sur FBI.`,
    ...(m.reply_to ? [`Une question ? Répondez simplement à ce message : il arrive au bureau ${ofClub(m.club_name)}.`] : []),
    '',
    'Sportivement,',
    signer,
  ].join('\n');
  const footer = [
    '', '', '—',
    `Envoyé avec Arceau, l’app des clubs de basket : ${m.referral_code ? `https://arceau.app/?r=${encodeURIComponent(m.referral_code)}&depuis=derogation` : 'https://arceau.app/?depuis=derogation'}`,
    `Ne plus recevoir ces messages : ${unsubscribe}`,
  ].join('\n');
  return { subject, text, fromName: `${m.club_name} via Arceau`, footer, unsubscribe, link };
}


/** D-137 : notre réponse à une demande de dérogation reçue d'un autre club (vouvoiement, réponses au bureau). */
function answerMail(m: PreMail, appUrl: string): BuiltMail {
  const tz = m.timezone || 'Europe/Paris';
  const base = appUrl.replace(/\/+$/, '');
  const link = `${base}/d/${m.token}`;
  const unsubscribe = `${link}?stop=1`;
  const title = `${m.team} ${m.is_home === false ? 'à' : '–'} ${m.opponent}`;
  const day = fmt(m.starts_at, tz, { weekday: 'short', day: 'numeric', month: 'short' });
  const ok = m.our_answer === 'accept';
  const what = m.kind === 'swap' ? 'l’inversion domicile / extérieur' : m.slots.length ? m.slots.map(s => `le ${longWhen(s.starts_at, tz)}${s.location ? ` (${s.location})` : ''}`).join(' ou ') : 'ce changement';
  const subject = `${m.club_name} : ${ok ? 'd’accord' : 'pas possible'} pour ${title} du ${day}`;
  const signer = m.signer && m.signer !== 'Compte' ? `${m.signer}, pour le bureau ${ofClub(m.club_name)}` : `Le bureau ${ofClub(m.club_name)}`;
  const competition = m.competition_name || COMPETITION[m.competition ?? ''] || '';
  const text = [
    'Bonjour,',
    '',
    'Vous nous avez demandé une dérogation pour cette rencontre :',
    '',
    `    ${title}`,
    `    ${[competition, longWhen(m.starts_at, tz)].filter(Boolean).join(' · ')}`,
    ...(m.location ? [`    ${m.location}`] : []),
    '',
    ok ? `Notre réponse : d’accord pour ${what}. Vous pouvez saisir la demande sur FBI ; si FBI nous demande notre accord, nous le donnerons.`
      : `Notre réponse : ce n’est malheureusement pas possible.`,
    ...(m.our_answer_note?.trim() ? ['', `« ${m.our_answer_note.trim()} »`] : []),
    '',
    ...(m.reply_to ? [`Une question ? Répondez simplement à ce message : il arrive au bureau ${ofClub(m.club_name)}.`, ''] : []),
    'Sportivement,',
    signer,
  ].join('\n');
  const footer = [
    '', '', '—',
    `Envoyé avec Arceau, l’app des clubs de basket : ${m.referral_code ? `https://arceau.app/?r=${encodeURIComponent(m.referral_code)}&depuis=derogation` : 'https://arceau.app/?depuis=derogation'}`,
    `Ne plus recevoir ces messages : ${unsubscribe}`,
  ].join('\n');
  return { subject, text, fromName: `${m.club_name} via Arceau`, footer, unsubscribe, link };
}

/** D-146 : « Les prévenir » : un petit changement annoncé (« nous allons saisir ce changement sur FBI »), sans attendre
 *  d'accord ; l'autre club répond « Bien reçu » ou « Ça nous pose problème » en 1 clic (vouvoiement, réponses au bureau). */
function prevMail(m: PreMail, appUrl: string): BuiltMail {
  const tz = m.timezone || 'Europe/Paris';
  const base = appUrl.replace(/\/+$/, '');
  const link = `${base}/d/${m.token}`;
  const unsubscribe = `${link}?stop=1`;
  const title = `${m.team} ${m.is_home === false ? 'à' : '–'} ${m.opponent}`;
  const s = m.slots[0]!;
  const day = (iso: string) => fmt(iso, tz, { weekday: 'short', day: 'numeric', month: 'short' });
  const sameDay = fmt(s.starts_at, tz, { year: 'numeric', month: '2-digit', day: '2-digit' }) === fmt(m.starts_at, tz, { year: 'numeric', month: '2-digit', day: '2-digit' });
  const moved = new Date(s.starts_at).getTime() !== new Date(m.starts_at).getTime();
  const hall = !!s.location && s.location !== m.location;
  const change = !moved && hall ? `change de salle (${s.location})` : sameDay ? `passe à ${hm(s.starts_at, tz)}${hall ? `, ${s.location}` : ''}`
    : `passe au ${day(s.starts_at)} à ${hm(s.starts_at, tz)}`;
  const subject = `${m.club_name} : ${title} du ${day(m.starts_at)} ${change}`;
  const competition = m.competition_name || COMPETITION[m.competition ?? ''] || '';
  const reason = [REASON[m.reason] ?? '', lower(m.reason_note?.trim() ?? '')].filter(Boolean);
  const why = reason.length === 2 ? `${reason[0]} (${reason[1]})` : reason[0] ?? '';
  const signer = m.signer && m.signer !== 'Compte' ? `${m.signer}, pour le bureau ${ofClub(m.club_name)}` : `Le bureau ${ofClub(m.club_name)}`;
  const text = [
    'Bonjour,',
    '',
    'Petit changement pour cette rencontre :',
    '',
    `    ${title}`,
    `    ${[competition, longWhen(m.starts_at, tz)].filter(Boolean).join(' · ')}`,
    ...(m.location ? [`    ${m.location}`] : []),
    '',
    'Nous allons saisir ce changement sur FBI :',
    `  • ${longWhen(s.starts_at, tz)}${s.location ? ` · ${s.location}` : ''}${!hall ? ' (même salle)' : m.location ? ` (au lieu de ${m.location})` : ''}`,
    '',
    ...(why ? [`Pourquoi : ${why}.`, ''] : []),
    'Rien à faire de votre côté, sauf si FBI vous demande votre accord.',
    'Bien reçu, ou un souci ? Dites-le en 1 clic, sans compte :',
    link,
    '',
    'Sportivement,',
    signer,
  ].join('\n');
  const footer = [
    '', '', '—',
    `Envoyé avec Arceau, l’app des clubs de basket : ${m.referral_code ? `https://arceau.app/?r=${encodeURIComponent(m.referral_code)}&depuis=derogation` : 'https://arceau.app/?depuis=derogation'}`,
    `Ne plus recevoir ces messages : ${unsubscribe}`,
  ].join('\n');
  return { subject, text, fromName: `${m.club_name} via Arceau`, footer, unsubscribe, link };
}
